import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { HERO_BG, SERVICE_MYO, SERVICE_SLEEP_ADULT, SERVICE_SLEEP_CHILD, SERVICE_TMJ } from '../assets/images';
import CTASection from '../components/CTASection';
import ServiceCard from '../components/ServiceCard';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import SleepApneaQuizSection from '../components/SleepApneaQuizSection';
import DangersCarousel from '../components/Dangerscarousel';
import doctor from '../assets/Doctor.webp';
import Myo_Functional from '../assets/Hero/MyoFunctional.svg';
import Pediatric from '../assets/Hero/Pediatric.svg';
import Sleep from '../assets/Hero/Sleep.svg';
import TMJ_Treatment from '../assets/Hero/tmj-treatment.svg';
import Sleepkids from '../assets/Hero/sleep-kids.svg';

import FAQ from "../components/FAQ";
import { GENERAL_FAQ } from "../data/faqData";


const { FiCheckCircle, FiStar, FiChevronLeft, FiChevronRight} = FiIcons;

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);

  const [activeTestimonial, setActiveTestimonial] = useState(0);


  // ✅ Helmet Added
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "MedicalOrganization",
    "name": "Dental Sleep Care",
    "url": "https://dentalsleepcare.in",
    "medicalSpecialty": [
      "Sleep Medicine",
      "Dentistry",
      "Pediatric Dentistry"
    ]
  };

  // ✅ Updated Hero Slides (SEO + Conversion)
  const heroSlides = [
    {
      title: "Advanced Sleep Apnea Care for Adults and Children",
      subtitle: "Advanced airway-focused solutions including MARPE and DOME to improve breathing, sleep quality, and long-term health.",
      image: Sleep,
      imageSmall: Sleep,   // replace with real smaller images
      imageMedium: Sleep,
      imageLarge: Sleep,
      link: "/sleep-apnea-adults"
    },
    {
      title: "Advanced Pediatric Airway Development",
      subtitle: "Early intervention to guide jaw growth, improve breathing, and prevent future sleep apnea complications.",
      image: Pediatric,
      imageSmall: Pediatric,   // replace with real smaller images
      imageMedium: Pediatric,
      imageLarge: Pediatric,
      link: "/sleep-apnea-children"
    },
    {
      title: "Myofunctional Therapy",
      subtitle: "Correct tongue posture and breathing patterns to support airway health and long-term stability.",
      image: Myo_Functional,
      imageSmall: Myo_Functional,   // replace with real smaller images
      imageMedium: Myo_Functional,
      imageLarge: Myo_Functional,
      link: "/myofunctional-therapy"
    },
    {
      title: "TMJ & Airway Treatment",
      subtitle: "Relieve jaw pain while addressing underlying airway issues that impact sleep and overall health.",
      image: TMJ_Treatment,
      imageSmall: TMJ_Treatment,   // replace with real smaller images
      imageMedium: TMJ_Treatment,
      imageLarge: TMJ_Treatment,
      link: "/tmj-treatment"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev === heroSlides.length - 1 ? 0 : prev + 1));
    }, 6000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const goToSlide = (index) => setCurrentSlide(index);

  // const nextSlide = () => setCurrentSlide((prev) => (prev === heroSlides.length - 1 ? 0 : prev + 1));
  // const prevSlide = () => setCurrentSlide((prev) => (prev === 0 ? heroSlides.length - 1 : prev - 1));
  // const goToSlide = (index) => setCurrentSlide(index);

  const services = [
    { title: "Myofunctional Therapy", image: Myo_Functional, link: "/myofunctional-therapy" },
    { title: "Sleep Apnea (Adults)", image: Sleep, link: "/sleep-apnea-adults" },
    { title: "Sleep Apnea (Children)", image: Sleepkids, link: "/sleep-apnea-children" },
    { title: "TMJ Treatment", image: TMJ_Treatment, link: "/tmj-treatment" },
  ];

  const reasons = [
  "Root-Cause Diagnosis of Sleep Apnea & Airway Disorders",
  "Advanced Sleep Apnea Treatments: MARPE, DOME & Myofunctional Therapy",
  "Early Intervention to Prevent Long-Term Sleep Apnea Complications",
  "3D Airway Analysis for Accurate Sleep Apnea Diagnosis"
];

  const testimonials = [
    { name: "Sarah M.", text: "The sleep therapy completely changed my life. I finally wake up feeling rested and energized." },
    { name: "David L.", text: "Dr. Reddy is fantastic with kids. My son's myofunctional therapy was an absolute success." },
    { name: "Emily R.", text: "Professional, caring, and highly knowledgeable about TMJ disorders. I highly recommend this practice!" }
  ];

  return (
    <div className="overflow-hidden">

       {/* ✅ Helmet Injected */}
      <Helmet>
        <title>Dental Sleep Care | Sleep Apnea & TMJ Specialists</title>
        <meta
          name="description"
          content="Expert care for sleep apnea, TMJ disorders, and airway health. Specialized treatments for adults and children at Dental Sleep Care."
        />
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>
      
      {/* 1. Dynamic Hero Slideshow Section */}
      <section className="relative w-full h-[85vh] md:h-screen bg-secondary overflow-hidden">
        
        {/* Background Images Layer */}
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? 'opacity-100 z-0' : 'opacity-0 z-0'
            }`}
          >
            <div className="absolute inset-0 w-full h-full">
              <picture className="absolute inset-0 w-full h-full">
                {/* WebP (optional but recommended) */}
                {slide.imageWebpSmall && (
                  <source
                    type="image/webp"
                    srcSet={`
                      ${slide.imageWebpSmall} 480w,
                      ${slide.imageWebpMedium} 768w,
                      ${slide.imageWebpLarge} 1200w
                    `}
                    sizes="100vw"
                  />
                )}

                <img
                  src={slide.image}
                  srcSet={`
                    ${slide.imageSmall || slide.image} 480w,
                    ${slide.imageMedium || slide.image} 768w,
                    ${slide.imageLarge || slide.image} 1200w
                  `}
                  sizes="100vw"
                  alt={slide.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[10000ms] ease-linear will-change-transform"
                  style={{
                    transform: index === currentSlide ? 'scale(1.1)' : 'scale(1)',
                  }}
                  loading={index === 0 ? "eager" : "lazy"}
                />
              </picture>
              {/* Overlays - Increased opacity for better text readability */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
            </div>
          </div>
        ))}

        {/* Slide Content Layer */}
        <div className="relative z-10 w-full h-full max-w-7xl mx-auto px-6 md:px-12 flex items-center">
          <div className="w-full">
            {heroSlides.map((slide, index) => (
              <div
                key={index}
                className={`absolute inset-x-6 md:inset-x-12 top-0 bottom-0 flex flex-col justify-center transition-all duration-700 ease-in-out ${
                  index === currentSlide
                    ? 'opacity-100 translate-y-0 pointer-events-auto'
                    : 'opacity-0 translate-y-8 pointer-events-none'
                }`}
              >
                {/* Constrain width to prevent text from hitting the edge of the screen */}
                <div className="max-w-2xl">
                  <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 md:mb-6 leading-tight tracking-tight drop-shadow-md">
                    {slide.title}
                  </h1>
                  <p className="text-base sm:text-lg md:text-xl text-gray-100 mb-8 md:mb-10 font-light leading-relaxed max-w-xl drop-shadow-sm">
                    {slide.subtitle}
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                    <Link
                      to={slide.link}
                      className="bg-primary hover:bg-white hover:text-primary text-white px-8 py-3 md:py-4 rounded-xl font-medium transition-all shadow-lg text-center text-lg flex items-center justify-center gap-2"
                    >
                      Learn More <SafeIcon icon={FiIcons.FiArrowRight} />
                    </Link>
                    <Link
                      to="https://api.whatsapp.com/send?phone=917013686917&text=APPOINTMENT"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white hover:text-primary text-white px-8 py-3 md:py-4 rounded-xl font-medium transition-all shadow-lg text-center text-lg"
                    >
                      Book Appointment
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Slideshow Indicators (Dots) */}
        <div className="absolute bottom-8 left-0 right-0 z-20 flex justify-center gap-3">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentSlide ? 'bg-primary w-8 h-3' : 'bg-white/50 hover:bg-white/80 w-3 h-3'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* 2. Services Grid */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-extrabold text-secondary mb-6 tracking-tight">Our Specialized Services</h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Comprehensive treatments tailored to improve your oral health, breathing, and overall quality of life. We focus on diagnosing and treating the root cause.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, idx) => (
              <ServiceCard key={idx} {...service} />
            ))}
          </div>
        </div>
      </section>

      {/* 3. About Preview */}
      <section className="py-24 px-6 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto">
          {/* Adjusted gap for perfect horizontal spacing without needing extra padding */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

            {/* Image Column */}
            <div className="relative group mx-auto w-full max-w-md lg:max-w-none">
              {/* Decorative Offset Background */}
              <div className="absolute inset-0 bg-primary/10 rounded-[2rem] transform translate-x-4 translate-y-4 lg:translate-x-6 lg:translate-y-6 transition-transform duration-500 group-hover:translate-x-3 group-hover:translate-y-3"></div>

              {/* Image Wrapper with Aspect Ratio to prevent distortion */}
              <div className="relative rounded-[2rem] overflow-hidden shadow-2xl transition-transform duration-500 group-hover:-translate-y-2 border border-gray-100 bg-gray-50 aspect-[4/5] sm:aspect-square lg:aspect-[4/5]">
                <img
                  src={doctor}
                  alt="Dr. Kranthi Kumar Reddy"
                  // object-top ensures the face/head is never cut off on different screen sizes
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Text Column */}
            <div className="flex flex-col justify-center">
              {/* Decorative Subheading */}
              <div className="inline-flex items-center gap-3 mb-4">
                <span className="w-10 h-0.5 bg-primary rounded-full"></span>
                <span className="text-primary font-bold tracking-widest uppercase text-sm">
                  Leading Specialist
                </span>
              </div>

              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-secondary mb-6 leading-tight tracking-tight">
                Meet Dr. Kranthi Kumar Reddy
              </h2>

              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Dedicated to airway-focused dentistry, Dr. Reddy specializes in treating sleep apnea, jaw development issues, and breathing disorders in both children and adults.
              </p>

              <p className="text-lg text-gray-600 mb-10 leading-relaxed">
                Dedicated to airway-focused dentistry, Dr. Reddy specializes in treating sleep apnea, jaw development issues, and breathing disorders in both children and adults.
              </p>

              {/* Button wrapped in a div to prevent it from stretching full width */}
              <div>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-3 bg-primary text-white font-medium text-lg px-8 py-4 rounded-xl hover:bg-secondary hover:-translate-y-1 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  Learn more about our practice <SafeIcon icon={FiIcons.FiArrowRight} />
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 4. Why Choose Us */}
      <section className="py-24 px-6 bg-secondary text-white relative overflow-hidden">
        {/* Subtle background gradient to add depth and improve contrast */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 to-transparent pointer-events-none"></div>

        {/* Decorative blur orbs */}
        <div className="absolute top-0 right-0 -mt-32 -mr-32 w-[30rem] h-[30rem] bg-white/10 rounded-full blur-[100px] pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 -mb-32 -ml-32 w-[30rem] h-[30rem] bg-black/10 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">

            {/* Text Content */}
            <div className="order-2 lg:order-1">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold mb-6 tracking-tight text-white drop-shadow-sm">
                Why Choose Our Practice?
              </h2>
              {/* Changed text-gray-300 to text-white/90 for much better readability on blue */}
              <p className="text-lg md:text-xl text-white/90 mb-10 leading-relaxed font-light">
                We focus on treating the root cause of airway and sleep issues, not just symptoms. From early pediatric intervention to advanced adult treatments like MARPE and DOME, our approach is personalized for long-term results.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6">
                {reasons.map((reason, idx) => (
                  <div
                    key={idx}
                    // Improved glassmorphism: stronger background, solid border, and nice hover effect
                    className="flex items-center gap-4 bg-white/10 p-5 rounded-2xl border border-white/20 backdrop-blur-md shadow-lg hover:bg-white/20 transition-all duration-300"
                  >
                    {/* Popping Icon: Solid white background with colored icon */}
                    <div className="bg-white w-10 h-10 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                      <SafeIcon icon={FiCheckCircle} className="text-secondary text-xl" />
                    </div>
                    {/* Changed text-gray-100 to solid white and increased font weight */}
                    <span className="font-semibold text-lg text-white leading-snug">{reason}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Staggered Image Grid */}
            <div className="order-1 lg:order-2 grid grid-cols-2 gap-4 sm:gap-6 items-center">
              {/* Column 1 - Pushed down slightly on larger screens */}
              <div className="flex flex-col transform md:translate-y-8 transition-transform duration-500 hover:-translate-y-2">
                <div className="rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white/10 bg-black/10 aspect-[4/5] relative">
                  {/* Added bg-black/10 as a fallback if the image path is broken */}
                  <img
                    src={Sleepkids}
                    alt="Sleep Apnea Kids"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                  />
                </div>
              </div>

              {/* Column 2 - Pushed up slightly on larger screens */}
              <div className="flex flex-col transform md:-translate-y-8 transition-transform duration-500 hover:-translate-y-10 mt-8 md:mt-0">
                <div className="rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white/10 bg-black/10 aspect-[4/5] relative">
                  <img
                    src={Pediatric}
                    alt="Pediatric Care"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                  />
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 5. Testimonials */}
      <section className="py-24 px-6 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">

            {/* Left Column: Headers & Statistics */}
            <div>
              {/* High contrast text for white background */}
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight text-secondary">
                <span className="text-accent2">Patient</span> Success Stories
              </h2>

              <p className="text-lg text-gray-600 mb-12 leading-relaxed max-w-lg">
                Hear how our holistic and specialized treatments have transformed the lives of our patients.
              </p>

              {/* Statistics Visuals */}
              <div className="w-full max-w-sm">
                <h3 className="font-bold text-xl text-secondary mb-6">Statistics</h3>
                <div className="space-y-6">
                  {/* Stat Bar 1 */}
                  <div className="w-full bg-gray-100 rounded-full h-3.5 shadow-inner">
                    <div className="bg-accent2 h-3.5 rounded-full" style={{ width: '90%' }}></div>
                  </div>
                  {/* Stat Bar 2 */}
                  <div className="w-full bg-gray-100 rounded-full h-3.5 shadow-inner">
                    <div className="bg-accent2 h-3.5 rounded-full" style={{ width: '80%' }}></div>
                  </div>
                  {/* Stat Bar 3 */}
                  <div className="w-full bg-gray-100 rounded-full h-3.5 shadow-inner">
                    <div className="bg-accent2 h-3.5 rounded-full" style={{ width: '85%' }}></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Active Testimonial Card */}
            <div className="relative mt-8 lg:mt-0">
              {/* Vibrant card background with strong shadow to float above the white page */}
              <div className="bg-primary rounded-[2rem] p-10 md:p-14 shadow-2xl flex flex-col justify-between min-h-[400px]">

                <div>
                  <p className="font-bold text-white/90 tracking-wider uppercase mb-6 text-sm">
                    Absolutely Outstanding
                  </p>
                  {/* Dynamic Testimonial Text */}
                  <p className="text-white text-lg md:text-2xl leading-relaxed mb-10 font-medium">
                    "{testimonials[activeTestimonial].text}"
                  </p>
                </div>

                {/* Bottom section of the card */}
                <div>
                  <hr className="border-white/20 mb-8" />
                  <div className="flex items-center gap-5">
                    {/* User Avatar Initial */}
                    <div className="w-14 h-14 rounded-full bg-white text-primary flex items-center justify-center font-bold text-2xl overflow-hidden shadow-md">
                      {testimonials[activeTestimonial].name.charAt(0)}
                    </div>
                    <div>
                      {/* Dynamic Testimonial Name */}
                      <p className="font-bold text-white text-lg">
                        {testimonials[activeTestimonial].name}
                      </p>
                      <p className="text-white/80 text-sm uppercase tracking-wider mt-1">
                        Patient
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Carousel Indicators (Dots) aligned to the bottom right */}
              <div className="absolute -bottom-10 right-4 flex gap-3">
                {testimonials.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveTestimonial(idx)}
                    className={`h-2 rounded-full transition-all duration-300 ${activeTestimonial === idx
                      ? 'bg-accent2 w-6'
                      : 'bg-gray-300 hover:bg-gray-400 w-2'
                      }`}
                    aria-label={`View testimonial ${idx + 1}`}
                  />
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      <DangersCarousel />

      {/* 6. Sleep Apnea Quiz Section */}
      <SleepApneaQuizSection />

      {/* 7. FAQ Section */}
      <FAQ data={GENERAL_FAQ} />

      {/* 8. CTA Section */}
      <CTASection />
    </div>
  );
}
